import React, { useEffect, useState, ReactNode } from 'react'
import router, { useRouter } from 'next/router'
import {
  Button,
  ColorType,
  Flex,
  Link,
  Text,
  theme,
} from '@clear/design-system'
import { PlanTypes, useEnrollmentContext } from '@contexts/EnrollmentContext'
import { Modal } from '@components/shared/Modal'

import { FeatureCard } from '@components/storybook/FeatureCard'
import Icon from '@components/shared/Icon'
import { usePricingConfigContext } from '@contexts/PricingContext'
import {
  formatPrice,
  getClearPlusBundlePricing,
  isFamDiscountExpired,
} from '@utils/pricing'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import * as Enrollment from '@clear-denver/tpc-openapi-typescript/build/v1/enrollment'
import { getXCLEARCorrelationInfo } from '@clear-denver/tpc-openapi-typescript/build/utils/get-xclear-correlation-info'
import { trackFunnelStep } from '@utils/trackFunnelStep'
import { useFlag } from '@hooks/useFlag'
import { YOUNG_ADULT_PROMOTION_PATH } from '@api/constants'
import useMedia from 'use-media'
import { useAuthTreatment } from '@hooks/useAuthPipe/lib/useAuthExperiement'
import { isTestMode } from '@utils/testMode'

const hideEnrollmentlFlow =
  process.env.NEXT_PUBLIC_HIDE_ENROLLMENT_FLOW === 'true'
const HomePageBanner: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <Flex
      height={['auto', 'auto', '30rem']}
      flexDirection={['column', 'row']}
      justifyContent="flex-start"
    >
      <Flex
        backgroundColor={'dhsBlue' as ColorType}
        width={['100%', '50%']}
        flexDirection="column"
        paddingLeft={['5%', '2%', '5%']}
        justifyContent="center"
        padding={['5%', '3%']}
      >
        <Text
          as="h1"
          fontSize={['1.75rem', '2.25rem', '3rem']}
          color="neutralsWhite"
          fontWeight="700"
          marginBottom="3%"
        >
          Enroll in TSA&nbsp;PreCheck<sup>&reg;</sup> Through CLEAR—
          <br />
          No Appointment Needed
        </Text>
        <Flex
          flexDirection={['column', 'row']}
          alignSelf="flex-start"
          justifyContent="flex-start"
          marginTop="2%"
          width={['100%', 'auto']}
        >
          {children}
        </Flex>
      </Flex>
      <img
        src="/tsa-airport-til.jpg"
        alt="Homepage Hero"
        width={668}
        height={523}
        className="flex w-full md:h-auto md:w-1/2 object-cover object-[100%_67%]"
      />
    </Flex>
  )
}

const YoungAdultPromoSection = () => {
  const youngAdultFlag = useFlag('youngAdultDiscount')?.showPromoItems
  const aboveMobile = theme?.breakpoints?.[0] as string
  const isAboveMobile = useMedia({ minWidth: aboveMobile })

  if (!youngAdultFlag) {
    return null
  }

  return (
    <Flex
      id="young-adult-promo-section"
      flexDirection="row"
      style={{
        backgroundColor: '#F6F7F2',
      }}
      width="full"
      height={['10rem', '275px', '275px']}
      className="flex overflow-hidden lg:justify-end"
    >
      <Flex
        alignItems="baseline"
        flexDirection="column"
        justifyContent="center"
        textAlign="start"
        width={['55%', '50%']}
        pl={[4, 10]}
        pr={[4, 6]}
        py={2}
      >
        <Text
          as="h2"
          color={'clearMidnightBlue' as ColorType}
          fontSize={[1, 4, 7]}
          fontWeight="700"
          marginTop={1}
        >
          TSA&nbsp;PreCheck<sup>®</sup> $20 Take Off
        </Text>
        <Text
          as="p"
          fontSize={[1, 3]}
          color={'clearMidnightBlue' as ColorType}
          textAlign="left"
          fontWeight={2}
          mt={[1, 5]}
        >
          For the month of May, get $20 off TSA PreCheck<sup>®</sup> if you're
          the age of 30 or under.
          <br />
          <Link
            target="_blank"
            href={YOUNG_ADULT_PROMOTION_PATH}
            color={'clearMidnightBlue' as ColorType}
          >
            <span className="font-semibold underline">Learn more</span>
          </Link>
        </Text>
      </Flex>
      <img
        src="/young-adult-module.png"
        alt="Young Adult Promo Image"
        style={{
          objectFit: 'cover',
          objectPosition: isAboveMobile ? '100% 27%' : 'center',
        }}
        className="h-full w-[45%] shrink-0 md:h-auto md:w-1/2"
      />
    </Flex>
  )
}

const Section1: React.FC<{
  treatment: ReturnType<typeof useAuthTreatment>
  showApplyModal: () => void
  handleSelectPlan: (plan: PlanTypes) => void
}> = ({ treatment, handleSelectPlan }) => {
  const { pricingConfig } = usePricingConfigContext()

  const formattedEnrollmentPrice =
    typeof pricingConfig?.enroll?.currentPrice === 'number'
      ? formatPrice(pricingConfig.enroll.currentPrice, 2)
      : 'loading...'
  const applyButtonText = `APPLY FOR ${formattedEnrollmentPrice}`

  const formattedRenwalPrice =
    typeof pricingConfig?.renew?.currentPrice === 'number'
      ? formatPrice(pricingConfig.renew.currentPrice, 2)
      : 'loading...'
  const renewButtonText = `RENEW ONLINE FOR ${formattedRenwalPrice}`

  return (
    <Flex
      flexDirection="column"
      pb={8}
      background="linear-gradient(180deg, rgba(248, 248, 248, 0) 0%, #F8F8F8 47.68%)"
    >
      <Flex
        flexDirection="column"
        alignSelf="center"
        justifyContent="space-between"
        width={['full']}
        maxWidth="90rem"
        mt={6}
        px={[6, 6, 6, 6, 12]}
      >
        <Flex
          flexDirection="column"
          alignItems="center"
          mb={7}
          data-cy="feature-header"
        >
          <Text
            as="p"
            variant="medium"
            fontSize={[4, 5, 6]}
            color={'clearMidnightBlue' as ColorType}
            textAlign="center"
          >
            TSA PreCheck<sup>®</sup> Keeps You Moving Through Airport Security
          </Text>
        </Flex>
        <Flex
          flexDirection={['column', 'row']}
          mb={5}
          justifyContent="space-between"
          width="100%"
        >
          <FeatureCard
            icon={
              <img src="/feature1.svg" alt="feature1" width={78} height={66} />
            }
            description="No need to remove shoes, laptops, 3-1-1 liquids, belts, or light jackets"
          />
          <Flex
            ml={[3, 7]}
            mr={[3, 7]}
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width={['90%', '30%']}
            maxWidth={['90%', '30%']}
            mb={[5, null]}
          >
            <Flex my={7} alignItems="center" justifyContent="center">
              <img
                src="/feature2.svg"
                alt="feature2"
                width="60px"
                height="60px"
              />
            </Flex>
            <Text
              as="h1"
              variant="regular"
              fontWeight="700"
              fontSize={[1, 3]}
              textAlign="center"
            >
              Children 17 and under can join an adult with TSA&nbsp;PreCheck
              <sup>®</sup> when TSA&nbsp;PreCheck<sup>®</sup> appears on the
              child's boarding pass
            </Text>
          </Flex>
          <FeatureCard
            icon={
              <img src="/feature3.svg" alt="feature3" width={77} height={76} />
            }
            description="Available with 90+ airlines at 200+ airports nationwide"
          />
        </Flex>
        <Flex
          flexDirection={['column', 'row']}
          justifyContent="center"
          alignItems="center"
          marginTop="2%"
        >
          <Button
            text={applyButtonText}
            id="homepage-apply-slowscroll-2"
            bg={'dhsBorderBlue' as ColorType}
            textProps={{ variant: 'regular', fontWeight: '600' }}
            mb={[5, 0]}
            mr={[0, 5]}
            width="300px"
            onClick={() => {
              const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
              dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
                payload: {
                  message: 'User clicked Apply For Price on Homepage',
                },
                metadata: {
                  kpi: KPI.USER_INTERACTION,
                  xCLEARCorrelationInfo,
                  url: router.asPath,
                  event: DiscreteEvent.HOME_PAGE_CLICK_APPLY_FOR_PRICE,
                },
              })

              /**  EXP: T2_AUTH_UNIFY_GA */
              treatment
                ? handleSelectPlan(PlanTypes.ENROLL)
                : router.push('/enroll/you')
            }}
          />
          <Button
            text={renewButtonText}
            variant="secondary"
            id="homepage-renew-slowscroll-2"
            width="300px"
            textProps={{
              variant: 'regular',
              color: 'clearMidnightBlue' as ColorType,
              fontWeight: '600',
            }}
            onClick={() => {
              const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
              dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
                payload: {
                  message: 'User clicked Renew For Price on Homepage',
                },
                metadata: {
                  kpi: KPI.USER_INTERACTION,
                  xCLEARCorrelationInfo,
                  url: router.asPath,
                  event: DiscreteEvent.HOME_PAGE_CLICK_RENEW_FOR_PRICE,
                },
              })

              /**  EXP: T2_AUTH_UNIFY_GA */
              treatment
                ? handleSelectPlan(PlanTypes.RENEW)
                : router.push('/renewal/eligibility/eligibility')
            }}
            mr={[0, 5]}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}

const Section2: React.FC<{
  treatment: ReturnType<typeof useAuthTreatment>
  handleSelectPlan: (plan: PlanTypes) => void
}> = ({ treatment, handleSelectPlan }) => {
  return (
    <Flex
      id="enroll-section-2"
      flexDirection="column"
      pb={8}
      backgroundColor={'dhsBlue' as ColorType}
      width={['full']}
    >
      <Flex
        alignItems="stretch"
        flexDirection="column"
        alignSelf="center"
        width={['full']}
        maxWidth="90rem"
        mt={6}
        px={[6, 6, 6, 6, 12]}
      >
        <Flex flexDirection="column" justifyContent="center" mb={6}>
          <Text
            as="p"
            fontSize={[4, 5, 6]}
            color="neutralsWhite"
            textAlign="center"
            fontWeight={2}
          >
            How To Apply
          </Text>
        </Flex>
        <Flex
          flexDirection={['column', 'row']}
          justifyContent="space-between"
          alignItems={['center', 'initial']}
          marginBottom="5%"
          color="neutralsWhite"
        >
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width={['90%', '30%']}
            textAlign="center"
            height="100%"
          >
            <img
              src="/how-to-apply1.svg"
              alt="how-to-apply1"
              width={60}
              height={61}
            />
            <Text as="p" fontSize={3} marginTop={5}>
              Start enrollment{' '}
              <Link
                color="neutralsWhite"
                onClick={() => {
                  /**  EXP: T3_AUTH_INCLUDE_DESIRED_PATH */
                  treatment
                    ? handleSelectPlan(PlanTypes.ENROLL)
                    : router.push('/enroll')
                }}
              >
                <span className="font-semibold underline">online</span>.
              </Link>
            </Text>
          </Flex>
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width={['90%', '30%']}
            marginTop={[5, 0]}
            height="100%"
            textAlign="center"
          >
            <img
              src="/how-to-apply2.svg"
              alt="how-to-apply2"
              width={60}
              height={61}
            />
            <Text as="p" fontSize={3} marginTop={5}>
              Head to any{' '}
              <Link target="_blank" href="/locations" color="neutralsWhite">
                <span className="font-semibold underline">
                  CLEAR enrollment location
                </span>
              </Link>{' '}
              to finish your application with our friendly Ambassadors in
              minutes.
            </Text>
          </Flex>
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width={['90%', '30%']}
            marginTop={[5, 0]}
            height="100%"
            textAlign="center"
          >
            <img
              src="/how-to-apply3.svg"
              alt="how-to-apply3"
              width={60}
              height={60}
            />
            <Text as="p" fontSize={3} marginTop={5}>
              You're all set! Once approved, receive your Known Traveler Number
              (KTN) in 3-5 days (some can take up to 60 days).
            </Text>
          </Flex>
        </Flex>
        <Flex flexDirection="column">
          <Text
            as="p"
            fontSize={3}
            color="neutralsWhite"
            textAlign="center"
            mb={5}
            fontWeight="600"
          >
            Travelers will need to add their KTN to their airline reservations
            to receive the TSA&nbsp;PreCheck<sup>®</sup> indicator on their
            boarding pass.
          </Text>
        </Flex>

        <Flex
          flexDirection="row"
          width="100%"
          border="1px solid #fff"
          borderRadius="12px"
          padding={3}
          justifyContent="center"
          alignItems="center"
          color="neutralsWhite"
        >
          <img
            width="35px"
            height="49px"
            src="/how-to-apply-banner-logo.svg"
            alt="how-to-apply-logo"
          />
          <Text as="p" variant="regular" fontSize={3} marginLeft={5}>
            Bring your U.S. passport or other accepted{' '}
            <Link
              href="/help#required-documents"
              color="neutralsWhite"
              target="_blank"
            >
              <span className="font-semibold underline">forms of ID</span>
            </Link>{' '}
            to finish your enrollment.
          </Text>
        </Flex>
      </Flex>
    </Flex>
  )
}

const Section3: React.FC<{
  setIsPlanBundled: (boolean: boolean) => void
  handleSelectPlan: (plan: PlanTypes) => void
}> = ({ setIsPlanBundled, handleSelectPlan }) => {
  const { pricingConfig } = usePricingConfigContext()
  const isPrepay =
    (useFlag('clearPlusPricingUpdate')?.variant ?? 'bundle') === 'prepay'
  const formattedEnrollmentPrice =
    typeof pricingConfig?.enroll?.currentPrice === 'number'
      ? formatPrice(pricingConfig?.enroll?.currentPrice, 2)
      : 'loading...'

  const formattedBundlePrice = getClearPlusBundlePricing({
    precheckPricing: {
      enroll: pricingConfig?.enroll?.currentPrice,
      renewal: pricingConfig?.renew?.currentPrice,
    },
  }).price.formatted
  return (
    <Flex width="100%" flexDirection="column">
      <Flex
        width="100%"
        height="auto"
        backgroundImage="url('./clear-airport-terminal.svg')"
        backgroundPosition="50% 78%"
        backgroundSize="cover"
      >
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="space-between"
          padding={10}
          height="100%"
        >
          <img
            src="./tsa-clear-logo.png"
            alt="tsa-clear-logo"
            width={407}
            height={67}
          />
          <Text
            as="h1"
            color={'clearMidnightBlue' as ColorType}
            fontSize={['1.75rem', '2.25rem', '3rem']}
            fontWeight="700"
            marginTop={5}
          >
            Get TSA PreCheck<sup>®</sup> for Free<sup>*</sup> <br /> When You
            Join CLEAR+
          </Text>
          <Text variant="regular" fontSize={3} fontWeight="700" marginTop={4}>
            Enroll in the CLEAR+ TSA PreCheck<sup>®</sup> bundle for the fastest
            way to your gate.
          </Text>
          <Button
            key="card-1"
            id="get-the-bundle-1"
            text="GET THE BUNDLE"
            bg={'dhsBorderBlue' as ColorType}
            textProps={{ variant: 'medium' }}
            marginTop={8}
            onClick={() => {
              setIsPlanBundled(true)
              handleSelectPlan(PlanTypes.ENROLL)
            }}
            data-cy="get-the-bundle-enroll-btn"
          />
        </Flex>
      </Flex>
      <Flex
        flexDirection="column"
        height="auto"
        justifyContent="center"
        alignItems="center"
        marginTop={5}
        marginBottom={5}
        width={['100%']}
      >
        <Flex
          width={['90%', '50%']}
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          marginTop={5}
        >
          <Flex
            backgroundImage="url(./tsa-pre-card-logo.svg)"
            width="45%"
            height={['29px', '30px', '45px']}
            backgroundRepeat="no-repeat"
            backgroundPosition="center"
            backgroundSize="contain"
          />
          <Flex
            backgroundImage="url(./ampersand.svg)"
            width={'10%'}
            height={['29px', '30px', '35px']}
            backgroundRepeat="no-repeat"
            backgroundPosition="center"
          />
          <Flex
            backgroundImage="url(./clear-plus.svg)"
            width="45%"
            height={['29px', '30px', '45px']}
            backgroundRepeat="no-repeat"
            backgroundPosition="center"
          />
        </Flex>
        <Flex
          flexDirection="row"
          width={['100%', '60%']}
          justifyContent="center"
          textAlign="center"
          marginTop={2}
        >
          <Flex
            flexDirection="column"
            width="50%"
            justifyContent="space-between"
          >
            <Flex
              padding={5}
              margin={3}
              border="2px solid"
              borderRadius="18px"
              justifyContent="center"
              borderColor="dhsBorderGreen"
            >
              No need to remove shoes, laptops, 3-1-1 liquids, belts, or light
              jackets.
            </Flex>
            <Flex
              padding={5}
              margin={3}
              border="2px solid"
              borderRadius="18px"
              justifyContent="center"
              borderColor="dhsBorderGreen"
            >
              An even easier security experience.
            </Flex>
          </Flex>
          <Flex
            flexDirection="column"
            width="50%"
            justifyContent="space-between"
          >
            <Flex
              padding={5}
              margin={3}
              border="2px solid"
              borderRadius="18px"
              justifyContent="center"
              borderColor="dhsBorderBlue"
            >
              Verify you are you at the CLEAR Pod.
            </Flex>
            <Flex
              padding={5}
              margin={3}
              border="2px solid"
              borderRadius="18px"
              justifyContent="center"
              borderColor="dhsBorderBlue"
            >
              CLEAR Ambassadors will bring you to the front of the TSA screening
              lane.
            </Flex>
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          marginTop={10}
        >
          <Text as="p" variant="regular" textAlign="center" fontSize="1.3rem">
            Now when you sign up for CLEAR+, you can{' '}
            <Text fontFamily="Inter-SemiBold">
              get TSA&nbsp;PreCheck
              <sup>®</sup> for free.
            </Text>
          </Text>
          <Text
            className="text-gray-700"
            style={{ textDecoration: 'line-through' }}
            mt={5}
            fontSize="1.3rem"
          >
            {formattedBundlePrice.combined.enroll}
          </Text>
          <Text
            as="h1"
            fontWeight="700"
            textAlign="center"
            color={'dhsBorderBlue' as ColorType}
            fontSize="3.5rem"
            mb={4}
          >
            {formattedBundlePrice.value}
          </Text>
          <Button
            key="card-1"
            id="get-the-bundle-2"
            text="GET THE BUNDLE"
            bg={'dhsBorderBlue' as ColorType}
            textProps={{ variant: 'medium' }}
            onClick={() => {
              setIsPlanBundled(true)
              handleSelectPlan(PlanTypes.ENROLL)
            }}
            width={['60%', '35%']}
            data-cy="get-the-bundle-enroll-btn2"
          />
          <Text
            as="p"
            variant="regular"
            textAlign="center"
            marginTop={5}
            marginBottom={isPrepay ? '1rem' : '2rem'}
          >
            5 years of TSA&nbsp;PreCheck<sup>®</sup> for{' '}
            <s className="text-gray-700">{formattedEnrollmentPrice}</s>{' '}
            <Text fontFamily="Inter-SemiBold">free</Text> + 1 year of CLEAR+ for{' '}
            <Text fontFamily="Inter-SemiBold">
              {formattedBundlePrice.value}
            </Text>
          </Text>
          {isPrepay && (
            <Text fontSize={0} textAlign="center" marginY={1}>
              When you pre-pay online and opt in for marketing communications
            </Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

const Section4: React.FC = () => {
  return (
    <Flex
      id="enroll-section-4"
      flexDirection={['column', 'row']}
      backgroundColor="neutralsGrayWash"
      width="full"
    >
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems="start"
        width={['100%', '45%']}
        textAlign="start"
        height={['275px', '394px']}
        p={6}
      >
        <Text
          as="p"
          fontSize={8}
          marginY={5}
          color={'clearMidnightBlue' as ColorType}
          fontFamily="Inter-Bold"
        >
          Families on the Fly
        </Text>
        <Text
          mb={5}
          color={'clearMidnightBlue' as ColorType}
          fontSize={4}
          paddingRight={7}
        >
          Get $15 off of one application when you apply for TSA PreCheck
          <sup>®</sup> with a family member, valid until December 31, 2025.{' '}
          <Link href="/help#family-discount" target="_blank">
            <span className="underline">
              <Text color={'clearMidnightBlue' as ColorType}>Learn more</Text>
            </span>
          </Link>
        </Text>
      </Flex>
      <Flex
        width={['100%', '55%']}
        height="394px"
        backgroundImage="url('./helena.jpg')"
        backgroundPosition="50% 30%"
        backgroundSize="cover"
      />
    </Flex>
  )
}

const Section5: React.FC = () => {
  const { pricingConfig } = usePricingConfigContext()

  const formattedMilitarySpousePrice =
    typeof pricingConfig?.militarySpouseEnroll?.currentPrice === 'number'
      ? formatPrice(pricingConfig.militarySpouseEnroll.currentPrice, 2)
      : 'loading...'

  return (
    <Flex
      id="enroll-section-5"
      flexDirection="column"
      mb={8}
      backgroundColor={'dhsBlue' as ColorType}
      width={['full']}
    >
      <Flex
        alignItems="stretch"
        flexDirection="column"
        alignSelf="center"
        width={['full']}
        maxWidth="90rem"
        mt={6}
        px={[6, 6, 6, 6, 12]}
      >
        <Flex flexDirection="column" justifyContent="center" mb={8}>
          <Text
            as="p"
            fontSize={[4, 5, 6]}
            color="neutralsWhite"
            textAlign="center"
            fontWeight={2}
          >
            Serve with Honor, Travel with Ease
          </Text>
        </Flex>
        <Flex
          flexDirection={['column', 'row']}
          justifyContent="space-between"
          alignItems={['center', 'initial']}
          marginBottom="5%"
          color="neutralsWhite"
        >
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width={['90%', '45%']}
            textAlign="center"
            height="100%"
          >
            <Icon name="military-star" alt="star" size={58} />
            <Text as="p" variant="medium" fontSize={4} marginY={5}>
              Military Survivor Families
            </Text>
            <Text>
              Complimentary TSA PreCheck<sup>®</sup> Enrollments and Renewals.
            </Text>
            <Text mb={5}> Must present valid TAPS letter.</Text>
            <Text as="p" variant="medium" mb={[5, null]}>
              Complimentary TSA PreCheck<sup>®</sup> for 5 years.<sup>**</sup>
            </Text>
          </Flex>
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width={['90%', '45%']}
            marginTop={[5, 0]}
            height="100%"
            textAlign="center"
          >
            <Icon name="military-person" alt="person" size={58} />
            <Text as="p" variant="medium" fontSize={4} marginY={5}>
              Military Spouses
            </Text>
            <Text>
              $25 off TSA PreCheck<sup>®</sup> Enrollments and Renewals.
            </Text>
            <Text mb={5}>
              Must present a physical DoW/Uniformed Services Identification
              (USID) card.
            </Text>
            <Text as="p" variant="medium">
              {formattedMilitarySpousePrice} for 5 years of TSA PreCheck
              <sup>®</sup>
              <sup>**</sup>
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}

const HomePage: React.FC = () => {
  const router = useRouter()
  const { setSelectedPlan, setAffiliate, setIsPlanBundled } =
    useEnrollmentContext()
  const { pricingConfig } = usePricingConfigContext()
  const treatment = useAuthTreatment()
  const isPrepay =
    (useFlag('clearPlusPricingUpdate')?.variant ?? 'bundle') === 'prepay'
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [modalBody, setModalBody] = useState<string | ReactNode>(
    'Please note: We are accepting online renewals only. At this time, we are not accepting new enrollment applications or in-person renewals.'
  )

  const formattedBundlePrice = getClearPlusBundlePricing({
    precheckPricing: {
      enroll: pricingConfig?.enroll?.currentPrice,
      renewal: pricingConfig?.renew?.currentPrice,
    },
  }).price.formatted

  // Show "loading..." until pricing config resolves — never render "$undefined"
  // and never assert a fee the BE hasn't confirmed (BE is the source of truth).
  const formattedEnrollmentPrice =
    typeof pricingConfig?.enroll?.currentPrice === 'number'
      ? formatPrice(pricingConfig.enroll.currentPrice, 2)
      : 'loading...'
  const formattedRenewalPrice =
    typeof pricingConfig?.renew?.currentPrice === 'number'
      ? formatPrice(pricingConfig.renew.currentPrice, 2)
      : 'loading...'

  const showApplyModal = () => {
    setModalBody(
      <Text variant="body" textAlign="center" color="neutralsTextGray">
        Please note: We are accepting online renewals only. At this time, we are
        not accepting new enrollment applications or in-person renewals.
      </Text>
    )
    setIsModalVisible(true)
  }
  const showModal = () => setIsModalVisible(true)
  const hideModal = () => setIsModalVisible(false)

  useEffect(() => {
    if (hideEnrollmentlFlow) showModal()
  }, [])

  const handleSelectPlan = async (plan: string) => {
    setSelectedPlan(plan)

    const query = isTestMode(router.query) ? '?test=true' : ''
    if (plan === PlanTypes.RENEW) {
      trackFunnelStep('renew', 1, 'homepage')
      const route = '/renewal/eligibility/eligibility' + query

      /** EXP: T1_AUTH_NO_REDIRECTS */
      treatment
        ? await router.push(
            {
              pathname: '/auth',
              query: {
                desiredPath: route,
              },
            },
            '/auth'
          )
        : await router.push(route)
    } else {
      trackFunnelStep('apply', 1, 'homepage')
      const route = '/enroll/you' + query

      /** EXP: T1_AUTH_NO_REDIRECTS */
      treatment
        ? await router.push(
            {
              pathname: '/auth',
              query: {
                desiredPath: route,
              },
            },
            '/auth'
          )
        : await router.push(route)
    }
  }

  useEffect(() => {
    if (router.query.affiliate) {
      setAffiliate(router.query.affiliate as string)
    }
  }, [router, setAffiliate])

  return (
    <>
      <Flex flexDirection="column" id="HomePageWrapper">
        <HomePageBanner>
          <Flex
            justifyContent="center"
            alignContent="flex-start"
            width={['100%', '20rem', 'auto']}
            flexDirection={['column', 'row']}
          >
            <>
              <Button
                key="card-1"
                id="homepage-apply-slowscroll-1"
                text="APPLY"
                variant="white"
                textProps={{
                  fontWeight: '600',
                  color: 'clearMidnightBlue' as ColorType,
                }}
                mr={[0, 5]}
                mb={[5, 0]}
                onClick={() => {
                  const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
                  dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
                    payload: {
                      message: 'User clicked Apply on Homepage',
                    },
                    metadata: {
                      kpi: KPI.USER_INTERACTION,
                      xCLEARCorrelationInfo,
                      url: router.asPath,
                      event: DiscreteEvent.HOME_PAGE_CLICK_APPLY,
                    },
                  })
                  handleSelectPlan(PlanTypes.ENROLL)
                }}
                data-cy="home-banner-enroll-btn"
              />
              <Button
                text="RENEW"
                variant="secondaryWhite"
                id="homepage-renew-slowscroll-1"
                textProps={{ fontWeight: '600' }}
                backgroundColor="neutralsWhite"
                onClick={() => {
                  const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
                  dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
                    payload: {
                      message: 'User clicked Renew on Homepage',
                    },
                    metadata: {
                      kpi: KPI.USER_INTERACTION,
                      xCLEARCorrelationInfo,
                      url: router.asPath,
                      event: DiscreteEvent.HOME_PAGE_CLICK_RENEW,
                    },
                  })
                  handleSelectPlan(PlanTypes.RENEW)
                }}
                data-cy="home-banner-renew-btn"
              />
            </>
          </Flex>
        </HomePageBanner>
        <Section1
          treatment={treatment}
          showApplyModal={showApplyModal}
          handleSelectPlan={handleSelectPlan}
        />
        <Section2 treatment={treatment} handleSelectPlan={handleSelectPlan} />
        <YoungAdultPromoSection />
        <Section3
          handleSelectPlan={handleSelectPlan}
          setIsPlanBundled={setIsPlanBundled}
        />
        {isFamDiscountExpired ? null : <Section4 />}
        <Section5 />
        <Flex
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          mb={8}
        >
          <Text
            width={['90%', '70%']}
            fontSize={0}
            textAlign="center"
            color="neutralsTextGray"
          >
            {isPrepay ? (
              <>
                <sup>*</sup>Terms apply. Offer available to non-CLEAR+ members.
                Must complete payment online for TSA PreCheck® through CLEAR (
                {formattedEnrollmentPrice} for new enrollments /{' '}
                {formattedRenewalPrice} for renewals, via{' '}
                <a
                  target="_blank"
                  href="https://tsaprecheckbyclear.tsa.dhs.gov/"
                  rel="noreferrer"
                >
                  https://tsaprecheckbyclear.tsa.dhs.gov/
                </a>
                ) to receive a unique promotional code to enroll in CLEAR+ at a
                reduced rate. CLEAR+ price for Members completing a new TSA
                PreCheck® enrollment:{' '}
                {formattedBundlePrice.diff.enrollmentPortion}/year. CLEAR+ price
                for Members renewing their TSA PreCheck® online:{' '}
                {formattedBundlePrice.diff.renewalPortion}/year. CLEAR+
                enrollment promotional code must be used within 7 days of
                payment for TSA PreCheck® enrollment or renewal. Must opt-in to
                CLEAR marketing during TSA PreCheck® enrollment. CLEAR+
                Membership renews at {formattedBundlePrice.value}/year. TSA
                PreCheck® enrollment subject to eligibility requirements of the
                Transportation Security Administration. More information can be
                found at{' '}
                <a
                  target="_blank"
                  href="https://www.tsa.gov/precheck"
                  rel="noreferrer"
                >
                  https://www.tsa.gov/precheck
                </a>
                . CLEAR+ is currently available to U.S. citizens, legal
                permanent residents 18 and older with a valid government-issued
                photo ID, as well as citizens of select international countries
                who have a valid passport. See eligible passports{' '}
                <a
                  target="_blank"
                  href="https://www.clearme.com/support/who-can-become-a-clear-plus-member"
                  rel="noreferrer"
                >
                  here.
                </a>
              </>
            ) : (
              <>
                Terms apply. *Upon completion of your TSA PreCheck<sup>®</sup>{' '}
                and CLEAR+ applications, CLEAR will issue you a refund of{' '}
                {formattedEnrollmentPrice} (the full cost of TSA PreCheck
                <sup>®</sup>). Offer available to non-CLEAR+ Members.{' '}
                {formattedEnrollmentPrice} will be charged in person for your
                TSA PreCheck<sup>®</sup>
                application. {formattedBundlePrice.value} will be charged for a
                1-year CLEAR+ membership. CLEAR+ Membership automatically renews
                annually for {formattedBundlePrice.value}. Must apply for both
                TSA PreCheck
                <sup>®</sup> through CLEAR and CLEAR+ within 7 days using the
                same email and must opt-in for the bundle. CLEAR+ is currently
                available to U.S. citizens, legal permanent residents 18 and
                older with a valid government-issued photo ID, as well as
                citizens of select international countries who have a valid
                passport. See eligible passports{' '}
                <a
                  target="_blank"
                  href="https://www.clearme.com/support/who-can-become-a-clear-plus-member"
                  rel="noreferrer"
                >
                  here.
                </a>{' '}
                CLEAR+ is not associated with the TSA PreCheck<sup>®</sup>{' '}
                Application Program.
              </>
            )}
          </Text>
          <Text
            width={['90%', '70%']}
            fontSize={0}
            textAlign="center"
            color="neutralsTextGray"
            mt={5}
          >
            <sup>**</sup>Military spouses with a valid military spouse ID are
            eligible for $25 off TSA PreCheck<sup>®</sup> enrollment.
            <br />
            Military survivor families with a valid TAPS letter are eligible for
            free TSA PreCheck<sup>®</sup> enrollment with TAPS letter or TSA
            acknowledgment form.
          </Text>
        </Flex>
        <Modal
          showModal={isModalVisible}
          setShowModal={setIsModalVisible}
          body={modalBody}
          canClose={true}
          successButtonText="Ok"
          successClick={() => hideModal()}
        />
      </Flex>
    </>
  )
}

export default HomePage
